import { useEffect, useState } from "react";
import { Download, Mail, Phone, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { api, errMsg, fileUrl, imgUrl } from "../lib/api";
import { STATUSES } from "../lib/constants";

const Row = ({ k, v }) => (v ? <p className="text-sm"><span className="text-slate-500">{k}: </span>{v}</p> : null);

const Items = ({ s }) =>
  s.type === "order" ? (
    <div className="space-y-2">
      {s.items.map((it, i) => (
        <div key={i} className="rounded-lg border border-white/10 bg-black/40 p-3" data-testid={`admin-detail-item-${i}`}>
          <p className="font-display text-lg font-bold uppercase">{it.item_type} <span className="text-[#E5202A]">× {it.quantity}</span></p>
          <Row k="Colors" v={it.colors} /><Row k="Placement" v={it.placement} /><Row k="Blank style #" v={it.style_number} /><Row k="Sizes" v={it.sizes} /><Row k="Notes" v={it.notes} />
          {it.designs?.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-3">
              {it.designs.map((d, j) => (
                <div key={d.preview_path} className="w-36 text-xs" data-testid={`admin-detail-item-${i}-design-${j}`}>
                  <a href={imgUrl(d.preview_url)} target="_blank" rel="noreferrer"><img src={imgUrl(d.preview_url)} alt={`${d.side} preview`} className="aspect-square w-full rounded-lg border border-white/10 object-cover" /></a>
                  <p className="mt-1 font-semibold">{d.side} preview</p>
                  {d.art_path && <a href={fileUrl(d.art_path)} className="flex items-center gap-1 text-[#60A5FA]" data-testid={`admin-detail-item-${i}-art-${j}`}><Download className="h-3 w-3" />{d.art_filename || "artwork"}</a>}
                  {d.texts?.map((t, n) => <p key={n} className="text-slate-300" data-testid={`admin-detail-item-${i}-text-${j}-${n}`}>“{t.text}” · {t.font} · <span style={{ color: t.color }}>■</span> {t.color}</p>)}
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
      <Row k="Order notes" v={s.notes} />
    </div>
  ) : (
    <div className="rounded-lg border border-white/10 bg-black/40 p-3">
      <Row k="Service" v={s.service} /><Row k="Quantity" v={s.quantity} />
      <p className="mt-2 whitespace-pre-wrap text-sm">{s.message}</p>
    </div>
  );

export const SubmissionDetail = ({ id, onClose, onChanged }) => {
  const [s, setS] = useState(null);
  const [notes, setNotes] = useState("");
  useEffect(() => {
    if (!id) return setS(null);
    api.get(`/admin/submissions/${id}`).then((r) => { setS(r.data); setNotes(r.data.admin_notes || ""); }).catch((e) => toast.error(errMsg(e)));
  }, [id]);

  const patch = async (body, msg) => {
    try {
      const { data } = await api.patch(`/admin/submissions/${id}`, body);
      setS(data);
      toast.success(msg);
      onChanged();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const del = async () => {
    if (!window.confirm(`Delete ${s.number}? This cannot be undone.`)) return;
    await api.delete(`/admin/submissions/${id}`);
    toast.success("Deleted");
    onClose();
    onChanged();
  };

  return (
    <Dialog open={!!id} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto border-white/10 bg-[#0E1018]" data-testid="admin-submission-detail">
        {!s ? <p className="text-slate-400">Loading...</p> : (
          <>
            <DialogHeader><DialogTitle className="font-display text-3xl font-black uppercase">{s.number} <span className="text-base capitalize text-slate-400">· {s.type}</span></DialogTitle></DialogHeader>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1">
                <p className="text-lg font-semibold" data-testid="admin-detail-customer-name">{s.customer.name}</p>
                <a href={`mailto:${s.customer.email}`} className="flex items-center gap-2 text-sm text-[#60A5FA]"><Mail className="h-4 w-4" />{s.customer.email}</a>
                <a href={`tel:${s.customer.phone}`} className="flex items-center gap-2 text-sm text-[#60A5FA]"><Phone className="h-4 w-4" />{s.customer.phone}</a>
                <Row k="Needed by" v={s.needed_by} /><Row k="Received" v={new Date(s.created_at).toLocaleString()} />
              </div>
              <div>
                <label className="label">Status</label>
                <select className="field" value={s.status} onChange={(e) => patch({ status: e.target.value }, "Status updated")} data-testid="admin-order-status-select">
                  {STATUSES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
            </div>
            <Items s={s} />
            <div>
              <p className="label">Artwork files ({s.files.length})</p>
              {s.files.map((f, i) => (
                <a key={f.path} href={fileUrl(f.path)} className="mb-2 flex items-center justify-between rounded-lg bg-white/5 px-3 py-2 text-sm hover:bg-white/10" data-testid={`admin-detail-file-${i}`}>
                  <span className="truncate">{f.filename}</span><Download className="h-4 w-4 text-[#E5202A]" />
                </a>
              ))}
            </div>
            <div>
              <label className="label">Internal notes (invoice #, mockup status, etc.)</label>
              <textarea className="field min-h-[100px]" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="admin-detail-notes-input" />
              <div className="mt-3 flex justify-between">
                <button onClick={del} className="flex items-center gap-2 text-sm text-slate-500 hover:text-red-400" data-testid="admin-detail-delete-button"><Trash2 className="h-4 w-4" />Delete</button>
                <button onClick={() => patch({ admin_notes: notes }, "Notes saved")} className="btn-red !py-2" data-testid="admin-detail-save-notes-button">Save Notes</button>
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
